import React, { useState, useEffect, useMemo } from "react";
import { Button } from "flowbite-react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import { MENU } from "../pages/menuItems";

// Build a flat list of all pages grouped by section from MENU
const buildPageSections = () => {
  const sections = [];
  MENU.forEach((item) => {
    if (item.children && item.children.length > 0) {
      sections.push({
        sectionKey: item.key,
        sectionLabel: item.label,
        pages: item.children.map((ch) => ({ key: ch.key, label: ch.label })),
      });
    } else if (item.to) {
      sections.push({
        sectionKey: item.key,
        sectionLabel: "Main",
        pages: [{ key: item.key, label: item.label }],
      });
    }
  });
  return sections;
};

const PAGE_SECTIONS = buildPageSections();

function PagePermissions() {
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/users/levels",
      );
      // Exclude admin (level 1) — admin always has full access
      setLevels((res.data || []).filter((l) => l.user_lvl !== 1));
    } catch {
      toast.error("Failed to fetch user levels.");
    } finally {
      setIsLoading(false);
    }
  };

  const selectLevel = async (lvl) => {
    setSelectedLevel(lvl);
    try {
      setIsLoading(true);
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/users/permissions/${lvl.user_lvl}`,
      );
      setChecked(new Set(res.data.permissions));
    } catch {
      toast.error("Failed to fetch permissions.");
      setChecked(new Set());
    } finally {
      setIsLoading(false);
    }
  };

  const toggle = (key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleSection = (section) => {
    const allKeys = section.pages.map((p) => p.key);
    const allChecked = allKeys.every((k) => checked.has(k));
    setChecked((prev) => {
      const next = new Set(prev);
      allKeys.forEach((k) => (allChecked ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const savePermissions = async () => {
    if (!selectedLevel) return;
    try {
      setIsLoading(true);
      await axios.post(
        "https://eps.elkomprintama.com/post/api/users/permissions",
        {
          user_lvl: selectedLevel.user_lvl,
          page_keys: Array.from(checked),
        },
      );
      toast.success(`Permissions saved for ${selectedLevel.level}!`);
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to save permissions.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="w-full rounded-lg bg-indigo-700">
        <label className="block uppercase text-white mb-2 tracking-wide text-5xl text-center font-bold">
          Page Permissions
        </label>
      </div>

      <div className="relative bg-gray-200 px-5 py-5 rounded-lg shadow-md mt-2">
        {isLoading && <LoadingOverlay message="Loading..." />}

        <div className="flex gap-5">
          {/* Left: User level list */}
          <div className="w-48 flex-shrink-0">
            <h3 className="text-xs font-bold uppercase text-gray-600 mb-2">
              User Levels
            </h3>
            <ul className="space-y-1">
              {levels.map((lvl) => (
                <li key={lvl.user_lvl}>
                  <button
                    onClick={() => selectLevel(lvl)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedLevel?.user_lvl === lvl.user_lvl
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-gray-700 hover:bg-indigo-50 border border-gray-300"
                    }`}
                  >
                    <span className="text-xs text-gray-400 mr-1">
                      [{lvl.user_lvl}]
                    </span>
                    {lvl.level}
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 mt-3 italic">
              Admin (Level 1) always has full access.
            </p>
          </div>

          {/* Right: Page checkboxes */}
          <div className="flex-1">
            {!selectedLevel ? (
              <p className="text-gray-500 text-sm mt-4">
                ← Select a user level to configure its page access.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold uppercase text-gray-700">
                    Pages for:{" "}
                    <span className="text-indigo-600">
                      {selectedLevel.level}
                    </span>
                  </h3>
                  <Button color="indigo" size="sm" onClick={savePermissions}>
                    Save Permissions
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {PAGE_SECTIONS.map((section) => {
                    const allChecked = section.pages.every((p) =>
                      checked.has(p.key),
                    );
                    const someChecked = section.pages.some((p) =>
                      checked.has(p.key),
                    );

                    return (
                      <div
                        key={section.sectionKey}
                        className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm"
                      >
                        {/* Section header with select-all checkbox */}
                        <label className="flex items-center gap-2 mb-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            ref={(el) => {
                              if (el)
                                el.indeterminate = someChecked && !allChecked;
                            }}
                            onChange={() => toggleSection(section)}
                            className="w-4 h-4 accent-indigo-600"
                          />
                          <span className="text-xs font-bold uppercase text-gray-600">
                            {section.sectionLabel}
                          </span>
                        </label>

                        {/* Individual page checkboxes */}
                        <ul className="space-y-1 pl-1">
                          {section.pages.map((page) => (
                            <li key={page.key}>
                              <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                  type="checkbox"
                                  checked={checked.has(page.key)}
                                  onChange={() => toggle(page.key)}
                                  className="w-3.5 h-3.5 accent-indigo-600"
                                />
                                <span className="text-xs text-gray-600 group-hover:text-gray-900">
                                  {page.label}
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5">
                  <Button fullSized color="indigo" onClick={savePermissions}>
                    Save Permissions for {selectedLevel.level}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default PagePermissions;
